//
// Must be called in the context of a Wrapper component from @googlemaps/react-wrapper.
// This is to ensure window.google.maps.places is defined.
// E.g. <Wrapper apiKey={envConfig.googleMapsAPIKey} render={render} libraries={['places']}>
//
import { FormControl, TextField } from "@mui/material"
import { Controller } from "react-hook-form"
import { toTitleCase } from "../../utils/utilityFunctions"

const { useRef } = require("react")
const { default: useGooglePlaceAutoComplete } = require("../../hooks/useGooglePlacesAutoComplete")
const { useEffect } = require("react")


/**
 * Just like TextFieldAutoCompleteControl except:
 * rules defaults to {required: true} and
 * fieldLabel gets a '*' appended to it.
 */
export function TextFieldAutoCompleteRequiredControl({fieldName, fieldLabel = '', control, errors,
                        rules={required: true},
                        placeholder,
                        textFieldError,
                        textFieldHelperText,
                        textFieldProps={},
                        controlSx={}, textFieldSx={},
                        onChange,
                        onSelectAddress,
                    }) {
    const theLabel = toTitleCase(fieldLabel || fieldName) + ' *'
    return (
        <TextFieldAutoCompleteControl
            fieldName={fieldName} fieldLabel={theLabel} control={control} errors={errors}
            rules={rules}
            placeholder={placeholder}
            textFieldError={textFieldError}
            textFieldHelperText={textFieldHelperText}
            textFieldProps={textFieldProps}
            controlSx={controlSx}
            textFieldSx={textFieldSx}
            onChange={onChange}
            onSelectAddress={onSelectAddress}
        />
    )
}

/**
 * 
 * @param {object} props 
 * @returns Form Control JSX
 * Behaves like a TextFieldControl but also provides Google Places Autocomplete.
 * When the user selects an address from the Google pop-up, calls onSelectAddress passing {addressComponents, geometry, formattedAddress, placeId}.
 */
export function TextFieldAutoCompleteControl({fieldName, fieldLabel = '', control, errors,
                        rules=null,
                        placeholder,
                        textFieldError,
                        textFieldHelperText,
                        textFieldProps={},
                        controlSx={}, textFieldSx={},
                        onChange,
                        onSelectAddress // Called with {addressComponents, geometry, formattedAddress, placeId} when user selects from places pop-up
                    }) {
    const theLabel = toTitleCase(fieldLabel || fieldName)
    const theLabelWithoutStar = theLabel.replace(/ \*$/, '')

    return (
        <FormControl sx={{my:1, ...controlSx}}>
            <Controller name={fieldName} control={control}
                rules={rules}
                render={({fieldState, field}) => (
                    <TextFieldAutoComplete
                        sx={{my:1, ...textFieldSx}}
                        label={theLabel}
                        placeholder={placeholder || theLabel}
                        value={field.value || ''}
                        error={textFieldError || !!errors[fieldName]}
                        helperText={fieldState?.error?.message || textFieldHelperText || (fieldState?.error && `${theLabelWithoutStar} is required`)}
                        onChange={(e) => {
                            field.onChange(e.target.value)
                            if (typeof onChange === 'function') {
                                onChange(e)
                            }
                        }}
                        onBlur={field.onBlur}
                        {...textFieldProps}
                        onSelectAddress={(place) => { // {addressComponents, geometry, formattedAddress, placeId}
                            field.onChange(place.formattedAddress)
                            onSelectAddress(place)
                        }}
                    />
                )}
            />
        </FormControl>
    )
}

/**
 * 
 * @param {object} props 
 * @returns TextField JSX
 * Binds a Google Places Autocomplete object to a TextField and returns the TextField JSX.
 * When the user selects an address from the Google pop-up, calls onSelectAddress passing {addressComponents, geometry, formattedAddress, placeId}.
 */
function TextFieldAutoComplete({sx, label, placeholder, value, error, helperText, onChange, onBlur, onSelectAddress}) {
    const inputElementRef = useRef()
    const {initAutoComplete, getFullPlace} = useGooglePlaceAutoComplete()
    const autoComplete = useRef()

    useEffect(() => {
        const initPlaces = async () => {
            console.log('initPlaces')
            const theAutoComplete = await initAutoComplete(inputElementRef.current, handleAddressSelect)
            console.log('initPlaces theAutoComplete', theAutoComplete)
            autoComplete.current = theAutoComplete
        }
        initPlaces()
    // eslint-disable-next-line
    }, [])


    const handleAddressSelect = async () => {
        console.log('handleAddressSelect autoComplete', autoComplete)
        const {addressComponents, geometry, formattedAddress, placeId} = await getFullPlace(autoComplete.current)
        console.log('handleAddressSelect {geometry, formattedAddress}', {geometry, formattedAddress})
        onSelectAddress({addressComponents, geometry, formattedAddress, placeId})
    }

    return (
        <TextField
            sx={sx}
            label={label}
            placeholder={placeholder}
            value={value}
            error={error}
            helperText={helperText}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={inputElementRef}
        />
    )
}

